import { Button, FeatureRadio } from '@praetorian-chariot/ui';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/form/Input';
import { Modal } from '@/components/Modal';
import {
  useCreatePlexTracClient,
  usePlexTracClient,
  useValidatePlexTracClient,
} from '@/hooks/usePlextrac';

interface PlexTracConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerEmail?: string;
}

export const PlexTracConnectionModal: React.FC<
  PlexTracConnectionModalProps
> = ({ isOpen, onClose, customerEmail }) => {
  const [mode, setMode] = useState<'select' | 'existing' | 'create' | null>(
    'select'
  );
  const [selectedOption, setSelectedOption] = useState<
    'existing' | 'create' | null
  >(null);
  const [existingClientId, setExistingClientId] = useState('');

  // Get current client ID to pass to validation (for revert on failure)
  const { clientId: currentClientId } = usePlexTracClient(customerEmail);

  const { mutateAsync: validatePlexTracClient, status: validateStatus } =
    useValidatePlexTracClient(customerEmail);
  const { mutateAsync: createPlexTracClient, status: createStatus } =
    useCreatePlexTracClient(customerEmail);

  const isValidating = validateStatus === 'pending';
  const isCreating = createStatus === 'pending';

  const handleReset = () => {
    setMode('select');
    setSelectedOption(null);
    setExistingClientId('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleProceedFromSelect = () => {
    if (selectedOption) {
      setMode(selectedOption);
    }
  };

  const handleValidateExisting = async () => {
    if (!existingClientId.trim()) {
      toast.error('Please enter a PlexTrac Client ID');
      return;
    }

    try {
      await validatePlexTracClient({
        clientId: existingClientId.trim(),
        currentClientId: currentClientId || undefined,
      });
      toast.success('PlexTrac Client ID connected successfully');
      handleClose();
    } catch (error) {
      // Show error message if not already handled by mutation hook
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to validate PlexTrac Client ID';
      toast.error(errorMessage);
      console.error('Failed to validate PlexTrac client:', error);
    }
  };

  const handleCreateNew = async () => {
    try {
      await createPlexTracClient({
        name: customerEmail || 'Chariot User',
        description: customerEmail
          ? `Auto-created for ${customerEmail}`
          : 'Auto-created PlexTrac client',
      });
      handleClose();
    } catch (error) {
      // Error is already handled by the mutation hook
      console.error('Failed to create PlexTrac client:', error);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        mode === 'select'
          ? 'Connect PlexTrac'
          : mode === 'existing'
            ? 'Enter Existing Client ID'
            : 'Create New Client'
      }
      subtitle={
        mode === 'select'
          ? 'Choose how you want to connect PlexTrac for this customer'
          : mode === 'existing'
            ? 'Enter the PlexTrac Client ID for this customer'
            : 'Create a new PlexTrac client for this customer'
      }
      size="lg"
      footer={
        mode === 'select'
          ? {
              text: 'Continue',
              onClick: handleProceedFromSelect,
              styleType: 'primary',
              disabled: !selectedOption,
              hideCancelButton: true,
              left: (
                <div className="flex gap-3">
                  <Button
                    label="Cancel"
                    styleType="tertiary"
                    size="md"
                    onClick={handleClose}
                  />
                </div>
              ),
            }
          : mode === 'existing'
            ? {
                text: isValidating ? 'Validating...' : 'Connect',
                onClick: handleValidateExisting,
                styleType: 'primary',
                disabled: isValidating || !existingClientId.trim(),
                hideCancelButton: true,
                left: (
                  <div className="flex gap-3">
                    <Button
                      label="Back"
                      styleType="secondary"
                      size="md"
                      onClick={handleReset}
                      disabled={isValidating}
                    />
                    <Button
                      label="Cancel"
                      styleType="tertiary"
                      size="md"
                      onClick={handleClose}
                      disabled={isValidating}
                    />
                  </div>
                ),
              }
            : mode === 'create'
              ? {
                  text: isCreating ? 'Creating...' : 'Create Client',
                  onClick: handleCreateNew,
                  styleType: 'primary',
                  disabled: isCreating,
                  hideCancelButton: true,
                  left: (
                    <div className="flex gap-3">
                      <Button
                        label="Back"
                        styleType="secondary"
                        size="md"
                        onClick={handleReset}
                        disabled={isCreating}
                      />
                      <Button
                        label="Cancel"
                        styleType="tertiary"
                        size="md"
                        onClick={handleClose}
                        disabled={isCreating}
                      />
                    </div>
                  ),
                }
              : undefined
      }
    >
      {mode === 'select' && (
        <div className="space-y-3">
          {/* Option 1: Use Existing Client ID */}
          <FeatureRadio
            id="existing"
            title="Use Existing Client ID"
            description="Connect to an existing PlexTrac client that was created before. This is the best option if this customer already has a PlexTrac client."
            checked={selectedOption === 'existing'}
            onChange={() => setSelectedOption('existing')}
            className="border-chariot-stroke/30 hover:border-chariot-brand-primary/50 w-full rounded-xl transition-colors"
          />

          {/* Option 2: Create New Client */}
          <FeatureRadio
            id="create"
            title="Create New Client"
            description="Create a new PlexTrac client for this customer. A new client ID will be automatically generated and connected."
            checked={selectedOption === 'create'}
            onChange={() => setSelectedOption('create')}
            className="border-chariot-stroke/30 hover:border-chariot-brand-primary/50 w-full rounded-xl transition-colors"
          />
        </div>
      )}

      {mode === 'existing' && (
        <div className="space-y-4">
          <Input
            label="PlexTrac Client ID"
            name="existingClientId"
            value={existingClientId}
            onChange={e => setExistingClientId(e.target.value)}
            placeholder="Enter Client ID"
            disabled={isValidating}
            info={{
              text: 'The Client ID must exist in PlexTrac and you must have proper access permissions.',
            }}
          />
        </div>
      )}

      {mode === 'create' && (
        <div className="space-y-4">
          <p className="text-chariot-text-secondary text-sm">
            A new PlexTrac client will be created with the following details:
          </p>
          <div className="border-chariot-stroke bg-chariot-background-secondary space-y-3 rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
              <span className="text-chariot-text-secondary text-sm font-medium">
                Client Name:
              </span>
              <span className="text-chariot-text-primary text-right text-sm">
                {customerEmail || 'Chariot User'}
              </span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <span className="text-chariot-text-secondary text-sm font-medium">
                Description:
              </span>
              <span className="text-chariot-text-primary text-right text-sm">
                {customerEmail
                  ? `Auto-created for ${customerEmail}`
                  : 'Auto-created PlexTrac client'}
              </span>
            </div>
          </div>
          <div className="border-chariot-stroke-info bg-chariot-status-info/10 flex items-start gap-3 rounded-lg border p-4">
            <div className="text-chariot-status-info mt-0.5">
              <svg className="size-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-chariot-text-secondary text-sm">
              The new client will be automatically configured and connected to
              this customer account.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};
